/**
 * Mail settings — Steps 10/11's admin surface. digitaladmin's conventions:
 * DRAFT-FIRST (nothing saves until Save Changes; the router guard owns
 * unsaved-changes), the masking convention (•••••••• shown when a secret
 * exists; leaving it untouched keeps the stored one), and the scope model —
 * this organisation's override vs the global row (platform admins only).
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiSend } from '../../lib/api';
import { useDirty } from '../../lib/dirty';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { qk } from '../../lib/queryClient';
import { MailSetupWizard } from './MailSetupWizard';
import { useAuth } from '../../lib/AuthContext';
import { Wand2, Send } from 'lucide-react';

interface MailRow {
  mode: 'graph' | 'smtp';
  graphTenantId: string | null;
  graphClientId: string | null;
  graphClientSecret: string | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecure: boolean;
  smtpUser: string | null;
  smtpPass: string | null;
  fromAddress: string | null;
  fromName: string | null;
  isActive: boolean;
}
interface MailSettingsResponse {
  org: MailRow | null;
  global?: MailRow | null; // absent entirely for non-platform admins
  effectiveScope: 'org' | 'global' | null;
  /** Step 171 — the sign-in registration's client ID, when there is one, so
   *  the wizard can offer to reuse it instead of sending somebody to Entra to
   *  copy a GUID. The ID only; the secret stays on the server. */
  signInAppClientId?: string | null;
  /** Step 171 — Microsoft credentials already present, so the mail wizard can
   *  skip the device-code sign-in entirely. */
  credentialsReady?: boolean;
}

const EMPTY: MailRow = {
  mode: 'graph',
  graphTenantId: null,
  graphClientId: null,
  graphClientSecret: null,
  smtpHost: null,
  smtpPort: null,
  smtpSecure: true,
  smtpUser: null,
  smtpPass: null,
  fromAddress: null,
  fromName: null,
  isActive: false,
};

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function MailSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.mailSettings,
    queryFn: () => apiFetch<MailSettingsResponse>('/api/admin/mail-settings'),
  });

  // Global is the DEFAULT; an org row is an override (Kai's model) — so the
  // platform admin lands on the global tab.
  // One organisation per deployment (2026-09-14): only the org row is edited.
  const scope = 'org' as const;
  const [draft, setDraft] = useState<MailRow>(EMPTY);
  const [savedNote, setSavedNote] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { me } = useAuth();
  const [testNote, setTestNote] = useState<string | null>(null);

  const stored = useMemo(
    () => (scope === 'org' ? (data?.org ?? EMPTY) : (data?.global ?? EMPTY)),
    [data, scope],
  );
  useEffect(() => setDraft(stored), [stored]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);
  useDirty(dirty);

  const save = useMutation({
    mutationFn: () => apiSend('/api/admin/mail-settings', 'PUT', { scope, ...draft }),
    onSuccess: (res) => {
      if (!res.ok) return;
      setSavedNote(true);
      setTimeout(() => setSavedNote(false), 2500);
      void queryClient.invalidateQueries({ queryKey: qk.mailSettings });
    },
  });

  if (isPending) {
    return (
      <div className="max-w-2xl space-y-3 p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="m-6 max-w-2xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load mail settings</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );
  }

  const set = <K extends keyof MailRow>(key: K, value: MailRow[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <div className="max-w-2xl p-6">
      <div className="mb-1 flex items-center gap-3">
        <h1 className="text-lg font-semibold">Mail</h1>
        <Button size="sm" variant="outline" onClick={() => setWizardOpen(true)}>
          <Wand2 /> Set up with the wizard
        </Button>
        {data?.effectiveScope && (
          <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-600 dark:text-emerald-400">
            sending via{' '}
            {data.effectiveScope === 'org' ? 'this organisation’s settings' : 'the global settings'}
          </span>
        )}
        {!data?.effectiveScope && (
          <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-xs text-amber-600 dark:text-amber-400">
            not configured — outbound email is off
          </span>
        )}
      </div>
      <p className="mb-4 text-sm text-muted-foreground">How this desk sends email.</p>

      <div className="space-y-4 rounded-lg border bg-card p-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Transport">
            <Select value={draft.mode} onChange={(e) => set('mode', e.target.value as MailRow['mode'])}>
              <option value="graph">Microsoft Graph (shared mailbox)</option>
              <option value="smtp">SMTP</option>
            </Select>
          </Field>
          <Field label="Use these settings">
            <Select
              value={draft.isActive ? 'yes' : 'no'}
              onChange={(e) => set('isActive', e.target.value === 'yes')}
            >
              <option value="no">Off</option>
              <option value="yes">On</option>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="From address" hint="The mailbox mail is sent as (and, later, polled for replies).">
            <Input
              type="email"
              value={draft.fromAddress ?? ''}
              onChange={(e) => set('fromAddress', e.target.value || null)}
              placeholder="support@yourdomain.org"
            />
          </Field>
          <Field label="From name">
            <Input
              value={draft.fromName ?? ''}
              onChange={(e) => set('fromName', e.target.value || null)}
              placeholder="4u Support"
            />
          </Field>
        </div>

        {draft.mode === 'graph' ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Entra tenant ID" hint="Directory (tenant) ID from the app registration.">
                <Input
                  value={draft.graphTenantId ?? ''}
                  onChange={(e) => set('graphTenantId', e.target.value || null)}
                />
              </Field>
              <Field label="Client ID">
                <Input
                  value={draft.graphClientId ?? ''}
                  onChange={(e) => set('graphClientId', e.target.value || null)}
                />
              </Field>
            </div>
            <Field
              label="Client secret"
              hint="Leave the dots untouched to keep the stored secret. Needs application permission Mail.Send, scoped to the mailbox with an ApplicationAccessPolicy."
            >
              <Input
                type="password"
                value={draft.graphClientSecret ?? ''}
                onChange={(e) => set('graphClientSecret', e.target.value || null)}
                placeholder="paste a client secret"
              />
            </Field>
          </>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Field label="SMTP host">
                  <Input
                    value={draft.smtpHost ?? ''}
                    onChange={(e) => set('smtpHost', e.target.value || null)}
                  />
                </Field>
              </div>
              <Field label="Port" hint="465 = implicit TLS; 587 = STARTTLS.">
                <Input
                  type="number"
                  value={draft.smtpPort ?? ''}
                  onChange={(e) => set('smtpPort', e.target.value ? Number(e.target.value) : null)}
                  placeholder="587"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Username">
                <Input
                  value={draft.smtpUser ?? ''}
                  onChange={(e) => set('smtpUser', e.target.value || null)}
                />
              </Field>
              <Field label="Password" hint="Leave the dots untouched to keep the stored password.">
                <Input
                  type="password"
                  value={draft.smtpPass ?? ''}
                  onChange={(e) => set('smtpPass', e.target.value || null)}
                />
              </Field>
            </div>
          </>
        )}

        {save.data && !save.data.ok && <p className="text-xs text-destructive">{save.data.error}</p>}
        {testNote && <p className="break-all text-xs text-muted-foreground">{testNote}</p>}
        <div className="flex items-center justify-end gap-3 border-t pt-3">
          {savedNote && <span className="text-xs text-emerald-600 dark:text-emerald-400">Saved.</span>}
          {dirty && !savedNote && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          <Button
            variant="outline"
            size="sm"
            disabled={dirty}
            title={dirty ? 'Save first, then test' : 'Sends a test email to you using the saved settings'}
            onClick={async () => {
              setTestNote('Sending…');
              const res = await apiSend<{ ok: boolean; error: string | null }>(
                '/api/admin/mail-settings/test',
                'POST',
                { scope, to: me?.user.email },
              );
              setTestNote(
                res.ok && res.body?.ok
                  ? 'Test sent — check your inbox.'
                  : (res.body?.error ?? res.error ?? 'failed'),
              );
            }}
          >
            <Send /> Send test
          </Button>
          <Button variant="ghost" disabled={!dirty || save.isPending} onClick={() => setDraft(stored)}>
            Discard
          </Button>
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
      <MailSetupWizard
        scope={scope}
        hasStoredSecret={!!(stored.graphClientSecret || stored.smtpPass)}
        signInAppClientId={data?.signInAppClientId ?? null}
        credentialsReady={data?.credentialsReady ?? false}
        open={wizardOpen}
        onClose={(finished) => {
          setWizardOpen(false);
          if (finished) void queryClient.invalidateQueries({ queryKey: qk.mailSettings });
        }}
      />
    </div>
  );
}
